import { FlightsClassTypes, REWARD_FOR_BASE_CABIN, type FareClassWithReward } from 'constants/flight'
import { ALL_CHARTER_FARE_CLASSES } from 'lib/charterFlights/charterOfferFareClassRestrictions'

/**
 * Canonical cash cabins for the Maldives charter (Economy Extra intentionally excluded).
 * Used as the fare-class dropdown options and the "All cabins" cheapest-bundle comparison set.
 */
export const CHARTER_CASH_CABINS: ReadonlyArray<FlightsClassTypes> = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.BUSINESS,
]

/**
 * Canonical selectable cash cabins for the Maldives charter cabin modal.
 * Economy Extra is included here, but callers should still feature-gate it with `lux_row`.
 */
export const CHARTER_SELECTABLE_CASH_CABINS: ReadonlyArray<FlightsClassTypes> = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.LUXROW,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.BUSINESS,
]

export function getCharterSelectableCashCabins(isLuxRowEnabled: boolean): ReadonlyArray<FlightsClassTypes> {
  return isLuxRowEnabled ? CHARTER_SELECTABLE_CASH_CABINS : CHARTER_CASH_CABINS
}

/**
 * Inserts reward-seat variants immediately after each cash cabin in the canonical charter order.
 * Cash cabins not in {@link CHARTER_SELECTABLE_CASH_CABINS} are omitted from the output.
 */
export function expandMaldivesCharterFareClassesWithRewards(
  fareClasses: ReadonlyArray<FlightsClassTypes>,
): Array<FareClassWithReward> {
  return CHARTER_SELECTABLE_CASH_CABINS
    .filter(fc => fareClasses.includes(fc))
    .flatMap((fc) => {
      const reward = REWARD_FOR_BASE_CABIN[fc]
      return reward ? [fc, reward] : [fc]
    })
}

/**
 * Full charter fare order for calendar "All classes" mode: every available cabin from economy
 * through business (cash then matching reward when present), without upsell slicing from the URL cabin.
 */
export function orderMaldivesCharterFareClassesFullPresentation(
  availableFareClasses: ReadonlyArray<FareClassWithReward>,
): Array<FareClassWithReward> {
  const available = new Set<FareClassWithReward>(availableFareClasses)
  return expandMaldivesCharterFareClassesWithRewards([...ALL_CHARTER_FARE_CLASSES]).filter(fc => available.has(fc))
}
